/* ==========================================================================
   Stanlee India — Google Reviews Section
   Vanilla JS. No dependencies, no external APIs. Reads assets/google-reviews-data.json
   ========================================================================== */

(function () {
  "use strict";

  var MAX_REVIEWS = 10;
  var TEXT_CLAMP_CHARS = 220;

  function escapeHtml(str) {
    if (!str) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function isPlaceholder(review) {
    if (!review) return true;
    var name = (review.reviewerName || "").toString();
    var text = (review.text || "").toString();
    return (
      !text.trim() ||
      name.indexOf("PASTE_") === 0 ||
      text.indexOf("PASTE_") === 0
    );
  }

  function initials(name) {
    if (!name) return "?";
    var parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map(function (p) { return p.charAt(0).toUpperCase(); }).join("");
  }

  function starsMarkup(rating, size) {
    rating = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    var out = "";
    for (var i = 1; i <= 5; i++) {
      var filled = i <= rating;
      out +=
        '<svg class="' +
        (filled ? "" : "stanlee-review-card__star--empty") +
        '" width="' + (size || 14) + '" height="' + (size || 14) + '" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path fill="currentColor" d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14l-5-4.87 6.91-1.01L12 2z"/>' +
        "</svg>";
    }
    return out;
  }

  var GOOGLE_G_ICON =
    '<svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true">' +
    '<path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>' +
    '<path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>' +
    '<path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"/>' +
    '<path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>' +
    "</svg>";

  function StanleeReviews(root) {
    this.root = root;
    this.jsonUrl = root.getAttribute("data-json-url");
    this.ratingOverride = root.getAttribute("data-rating-override");
    this.viewport = root.querySelector("[data-viewport]");
    this.track = root.querySelector("[data-track]");
    this.prevBtn = root.querySelector("[data-prev]");
    this.nextBtn = root.querySelector("[data-next]");
    this.statsEl = root.querySelector("[data-stats]");
    this.emptyState = root.querySelector("[data-empty-state]");
    this.lightbox = document.querySelector("[data-lightbox]");

    this.reviews = [];
    this.cloneCount = 0;
    this.currentIndex = 0;
    this.cardStep = 0;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartTranslate = 0;
    this.currentTranslate = 0;

    this.lightboxPhotos = [];
    this.lightboxIndex = 0;
    this.lightboxTriggerEl = null;

    this.init();
  }

  StanleeReviews.prototype.init = function () {
    var self = this;
    fetch(this.jsonUrl, { credentials: "same-origin" })
      .then(function (res) {
        if (!res.ok) throw new Error("Failed to load reviews data (" + res.status + ")");
        return res.json();
      })
      .then(function (data) {
        var all = Array.isArray(data.reviews) ? data.reviews : [];
        self.reviews = all.filter(function (r) { return !isPlaceholder(r); }).slice(0, MAX_REVIEWS);
        self.totalReviewCount = data.totalReviewCount || null;

        if (!self.reviews.length) {
          self.root.classList.remove("is-loading");
          if (self.emptyState) self.emptyState.hidden = false;
          var track = self.track;
          if (track) track.innerHTML = "";
          return;
        }

        self.renderStats();
        self.renderCards();
        self.root.classList.remove("is-loading");

        requestAnimationFrame(function () {
          self.setupCarousel();
          self.setupLightbox();
          self.setupImageLoading();
        });
      })
      .catch(function (err) {
        console.error("[Stanlee Reviews]", err);
        self.root.classList.remove("is-loading");
        if (self.emptyState) self.emptyState.hidden = false;
      });
  };

  /* ------------------------------ Stats header ------------------------------ */

  StanleeReviews.prototype.renderStats = function () {
    var reviews = this.reviews;
    var avgRounded;

    var override = parseFloat(this.ratingOverride);
    if (this.ratingOverride && !isNaN(override) && override > 0 && override <= 5) {
      avgRounded = Math.round(override * 10) / 10;
    } else {
      var avg = reviews.reduce(function (sum, r) { return sum + (Number(r.rating) || 0); }, 0) / reviews.length;
      avgRounded = Math.round(avg * 10) / 10;
    }

    this.statsEl.innerHTML =
      '<div class="stanlee-reviews__stats-content">' +
      '<span class="stanlee-reviews__stats-stars" aria-hidden="true">' + starsMarkup(avgRounded, 18) + "</span>" +
      '<span class="stanlee-reviews__stats-num">' + avgRounded.toFixed(1) + " Average Rating</span>" +
      '<span class="stanlee-reviews__stats-divider"></span>' +
      '<span class="stanlee-reviews__stats-badge">' + GOOGLE_G_ICON + " Google Verified Reviews</span>" +
      "</div>";
  };

  /* -------------------------------- Card render -------------------------------- */

  StanleeReviews.prototype.buildCard = function (review, index) {
    var li = document.createElement("li");
    li.className = "stanlee-review-card is-visible";
    li.setAttribute("role", "listitem");
    li.style.animationDelay = Math.min(index * 0.06, 0.4) + "s";

    var hasPhotos = Array.isArray(review.photos) && review.photos.length > 0;
    var photoHtml = "";
    if (hasPhotos) {
      var firstPhoto = review.photos[0];
      photoHtml =
        '<div class="stanlee-review-card__photo" data-open-gallery tabindex="0" role="button" ' +
        'aria-label="Open photo gallery for ' + escapeHtml(review.reviewerName) + '\'s review, ' + review.photos.length + ' photo' + (review.photos.length > 1 ? "s" : "") + '">' +
        '<img data-lazy-src="' + escapeHtml(firstPhoto) + '" alt="Photo from ' + escapeHtml(review.reviewerName) + '\'s review" loading="lazy" />' +
        (review.photos.length > 1
          ? '<span class="stanlee-review-card__photo-count">' +
            '<svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4 4h16v14H4V4zm2 2v10h12V6H6zm3 2h6v2H9V8zm0 4h4v2H9v-2z"/></svg>' +
            review.photos.length +
            "</span>"
          : "") +
        "</div>";
    }

    var avatarHtml = review.reviewerImage
      ? '<img class="stanlee-review-card__avatar" data-lazy-src="' + escapeHtml(review.reviewerImage) + '" alt="" loading="lazy" />'
      : '<div class="stanlee-review-card__avatar-fallback" aria-hidden="true">' + escapeHtml(initials(review.reviewerName)) + "</div>";

    var text = review.text || "";
    var isLong = text.length > TEXT_CLAMP_CHARS;

    li.innerHTML =
      photoHtml +
      '<div class="stanlee-review-card__body">' +
      '<div class="stanlee-review-card__top">' +
      avatarHtml +
      '<div class="stanlee-review-card__identity">' +
      '<div class="stanlee-review-card__name-row">' +
      '<span class="stanlee-review-card__name">' + escapeHtml(review.reviewerName) + "</span>" +
      (review.verified !== false
        ? '<span class="stanlee-review-card__verified" title="Verified Google review">' + GOOGLE_G_ICON + "</span>"
        : "") +
      "</div>" +
      '<div class="stanlee-review-card__date">' + escapeHtml(review.date || "") + "</div>" +
      "</div>" +
      "</div>" +
      '<div class="stanlee-review-card__stars" aria-label="' + (Number(review.rating) || 0) + ' out of 5 stars">' + starsMarkup(review.rating) + "</div>" +
      '<p class="stanlee-review-card__text' + (isLong ? "" : "") + '" data-review-text>' + escapeHtml(text) + "</p>" +
      (isLong ? '<button type="button" class="stanlee-review-card__readmore" data-readmore aria-expanded="false">Read more</button>' : "") +
      "</div>";

    if (hasPhotos) {
      var photos = review.photos.slice();
      var name = review.reviewerName;
      var openFn = this.openLightbox.bind(this);
      var photoEl = li.querySelector("[data-open-gallery]");
      photoEl.addEventListener("click", function () {
        openFn(photos, 0, name, photoEl);
      });
      photoEl.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openFn(photos, 0, name, photoEl);
        }
      });
    }

    return li;
  };

  StanleeReviews.prototype.renderCards = function () {
    var self = this;
    this.track.innerHTML = "";
    var fragment = document.createDocumentFragment();
    this.reviews.forEach(function (review, i) {
      fragment.appendChild(self.buildCard(review, i));
    });
    this.track.appendChild(fragment);

    // Build infinite-loop clones (front/back) if we have enough cards
    var total = this.reviews.length;
    this.cloneCount = Math.min(4, total);
    if (this.cloneCount > 0 && total > this.cloneCount) {
      var originals = Array.prototype.slice.call(this.track.children);
      var headClones = originals.slice(0, this.cloneCount).map(function (n) {
        var c = n.cloneNode(true);
        c.setAttribute("aria-hidden", "true");
        c.classList.remove("is-visible");
        return c;
      });
      var tailClones = originals.slice(-this.cloneCount).map(function (n) {
        var c = n.cloneNode(true);
        c.setAttribute("aria-hidden", "true");
        c.classList.remove("is-visible");
        return c;
      });
      tailClones.forEach(function (c) { self.track.insertBefore(c, self.track.firstChild); });
      headClones.forEach(function (c) { self.track.appendChild(c); });

      // Re-bind gallery click handlers on clones (buildCard bound listeners are lost on cloneNode)
      this.rebindCloneGalleries();
    } else {
      this.cloneCount = 0;
    }
  };

  StanleeReviews.prototype.rebindCloneGalleries = function () {
    var self = this;
    var nodes = this.track.querySelectorAll('[data-open-gallery][aria-hidden="true"], .stanlee-review-card[aria-hidden="true"] [data-open-gallery]');
    // Simpler: re-query all cloned cards' gallery triggers by walking clones
    var clonedCards = this.track.querySelectorAll('.stanlee-review-card[aria-hidden="true"]');
    clonedCards.forEach(function (card, idx) {
      var trigger = card.querySelector("[data-open-gallery]");
      if (!trigger) return;
      var nameEl = card.querySelector(".stanlee-review-card__name");
      var name = nameEl ? nameEl.textContent : "";
      // Find matching review by name to recover photos array (best-effort; fine since clones only mirror first/last N)
      var match = self.reviews.find(function (r) { return r.reviewerName === name; });
      var photos = match && Array.isArray(match.photos) ? match.photos : [];
      if (!photos.length) return;
      trigger.addEventListener("click", function () {
        self.openLightbox(photos, 0, name, trigger);
      });
      trigger.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          self.openLightbox(photos, 0, name, trigger);
        }
      });
    });
  };

  /* -------------------------------- Carousel -------------------------------- */

  StanleeReviews.prototype.getCardStep = function () {
    var card = this.track.querySelector(".stanlee-review-card");
    if (!card) return 0;
    var style = window.getComputedStyle(this.track);
    var gap = parseFloat(style.columnGap || style.gap || "0") || 0;
    return card.getBoundingClientRect().width + gap;
  };

  StanleeReviews.prototype.setupCarousel = function () {
    var self = this;
    this.cardStep = this.getCardStep();
    this.currentIndex = this.cloneCount; // first real card
    this.moveTo(this.currentIndex, false);

    this.prevBtn.addEventListener("click", function () { self.step(-1); });
    this.nextBtn.addEventListener("click", function () { self.step(1); });

    this.viewport.addEventListener("keydown", function (e) {
      if (e.key === "ArrowLeft") { e.preventDefault(); self.step(-1); }
      if (e.key === "ArrowRight") { e.preventDefault(); self.step(1); }
    });

    // Pointer drag (mouse + touch unified)
    this.track.addEventListener("pointerdown", function (e) {
      self.onDragStart(e);
    });
    window.addEventListener("pointermove", function (e) {
      if (self.isDragging) self.onDragMove(e);
    });
    window.addEventListener("pointerup", function (e) {
      if (self.isDragging) self.onDragEnd(e);
    });
    window.addEventListener("pointercancel", function () {
      if (self.isDragging) self.onDragEnd();
    });

    this.track.addEventListener("dragstart", function (e) { e.preventDefault(); });

    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        self.cardStep = self.getCardStep();
        self.moveTo(self.currentIndex, false);
      }, 150);
    });

    this.track.addEventListener("transitionend", function () {
      self.checkLoopBoundary();
    });
  };

  StanleeReviews.prototype.step = function (dir) {
    this.currentIndex += dir;
    this.moveTo(this.currentIndex, true);
  };

  StanleeReviews.prototype.moveTo = function (index, animate) {
    this.cardStep = this.cardStep || this.getCardStep();
    var translate = -(index * this.cardStep);
    this.currentTranslate = translate;
    this.track.classList.toggle("is-instant", !animate);
    this.track.style.transform = "translateX(" + translate + "px)";
    if (!animate) {
      // force reflow to apply instant position before re-enabling transitions
      void this.track.offsetHeight;
      this.track.classList.remove("is-instant");
    }
  };

  StanleeReviews.prototype.checkLoopBoundary = function () {
    var total = this.reviews.length;
    if (!this.cloneCount) return;
    if (this.currentIndex < this.cloneCount) {
      this.currentIndex += total;
      this.moveTo(this.currentIndex, false);
    } else if (this.currentIndex >= this.cloneCount + total) {
      this.currentIndex -= total;
      this.moveTo(this.currentIndex, false);
    }
  };

  StanleeReviews.prototype.onDragStart = function (e) {
    // Don't hijack pointer events that originate on interactive controls
    // (Read More button, photo/gallery trigger, links, etc.) — let them click normally.
    if (e.target.closest("[data-readmore], [data-open-gallery], button, a")) {
      return;
    }
    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragStartTranslate = this.currentTranslate;
    this.track.classList.add("is-dragging");
    this.track.setPointerCapture && this.track.setPointerCapture(e.pointerId);
  };

  StanleeReviews.prototype.onDragMove = function (e) {
    var delta = e.clientX - this.dragStartX;
    var translate = this.dragStartTranslate + delta;
    this.currentTranslate = translate;
    this.track.style.transform = "translateX(" + translate + "px)";
  };

  StanleeReviews.prototype.onDragEnd = function () {
    this.isDragging = false;
    this.track.classList.remove("is-dragging");
    this.cardStep = this.cardStep || this.getCardStep();
    var nearest = Math.round(-this.currentTranslate / this.cardStep);
    this.currentIndex = nearest;
    this.moveTo(this.currentIndex, true);
  };

  /* -------------------------------- Read more -------------------------------- */
  /* Bound once, globally, via bindReadMoreDelegation() at the bottom of this file. */

  /* -------------------------------- Lazy images -------------------------------- */

  StanleeReviews.prototype.setupImageLoading = function () {
    var imgs = this.track.querySelectorAll("img[data-lazy-src]");
    var loadImg = function (img) {
      var src = img.getAttribute("data-lazy-src");
      if (!src) return;
      img.src = src;
      img.addEventListener("load", function () { img.classList.add("is-loaded"); });
      img.addEventListener("error", function () { img.closest(".stanlee-review-card__photo") && (img.closest(".stanlee-review-card__photo").style.display = "none"); });
      img.removeAttribute("data-lazy-src");
    };

    if ("IntersectionObserver" in window) {
      var observer = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              loadImg(entry.target);
              obs.unobserve(entry.target);
            }
          });
        },
        { rootMargin: "200px" }
      );
      imgs.forEach(function (img) { observer.observe(img); });
    } else {
      imgs.forEach(loadImg);
    }
  };

  /* -------------------------------- Lightbox -------------------------------- */

  StanleeReviews.prototype.setupLightbox = function () {
    var self = this;
    var lb = this.lightbox;
    if (!lb || lb.dataset.bound) return;
    lb.dataset.bound = "true";

    this.lbImage = lb.querySelector("[data-lightbox-image]");
    this.lbCounter = lb.querySelector("[data-lightbox-counter]");
    this.lbClose = lb.querySelector("[data-lightbox-close]");
    this.lbPrev = lb.querySelector("[data-lightbox-prev]");
    this.lbNext = lb.querySelector("[data-lightbox-next]");
    this.lbBackdrop = lb.querySelector("[data-lightbox-backdrop]");

    this.lbClose.addEventListener("click", function () { self.closeLightbox(); });
    this.lbBackdrop.addEventListener("click", function () { self.closeLightbox(); });
    this.lbPrev.addEventListener("click", function () { self.lightboxStep(-1); });
    this.lbNext.addEventListener("click", function () { self.lightboxStep(1); });

    document.addEventListener("keydown", function (e) {
      if (!lb.classList.contains("is-open")) return;
      if (e.key === "Escape") self.closeLightbox();
      if (e.key === "ArrowLeft") self.lightboxStep(-1);
      if (e.key === "ArrowRight") self.lightboxStep(1);
      if (e.key === "Tab") self.trapFocus(e);
    });
  };

  StanleeReviews.prototype.openLightbox = function (photos, startIndex, altName, triggerEl) {
    if (!photos || !photos.length) return;
    this.lightboxPhotos = photos;
    this.lightboxIndex = startIndex || 0;
    this.lightboxAlt = altName || "";
    this.lightboxTriggerEl = triggerEl || null;

    var lb = this.lightbox;
    lb.hidden = false;
    lb.setAttribute("aria-hidden", "false");
    requestAnimationFrame(function () { lb.classList.add("is-open"); });

    this.renderLightboxImage();
    document.body.style.overflow = "hidden";
    this.lbClose.focus();
  };

  StanleeReviews.prototype.closeLightbox = function () {
    var lb = this.lightbox;
    var self = this;
    lb.classList.remove("is-open");
    lb.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    setTimeout(function () { lb.hidden = true; }, 350);
    if (this.lightboxTriggerEl) this.lightboxTriggerEl.focus();
  };

  StanleeReviews.prototype.lightboxStep = function (dir) {
    var len = this.lightboxPhotos.length;
    this.lightboxIndex = (this.lightboxIndex + dir + len) % len;
    this.renderLightboxImage();
  };

  StanleeReviews.prototype.renderLightboxImage = function () {
    var src = this.lightboxPhotos[this.lightboxIndex];
    this.lbImage.src = src;
    this.lbImage.alt = this.lightboxAlt ? "Photo " + (this.lightboxIndex + 1) + " from " + this.lightboxAlt + "'s review" : "Review photo";
    var multi = this.lightboxPhotos.length > 1;
    this.lbPrev.style.display = multi ? "flex" : "none";
    this.lbNext.style.display = multi ? "flex" : "none";
    this.lbCounter.textContent = multi ? this.lightboxIndex + 1 + " / " + this.lightboxPhotos.length : "";
  };

  StanleeReviews.prototype.trapFocus = function (e) {
    var focusable = this.lightbox.querySelectorAll("button");
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  /* -------------------------------- Read more (global, delegated, bound once) -------------------------------- */

  function bindReadMoreDelegation() {
    if (document.documentElement.dataset.stanleeReadmoreBound === "true") return;
    document.documentElement.dataset.stanleeReadmoreBound = "true";

    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-readmore]");
      if (!btn) return;
      var textEl = btn.previousElementSibling;
      if (!textEl || !textEl.classList.contains("stanlee-review-card__text")) return;

      var expanded = textEl.classList.toggle("is-expanded");
      btn.textContent = expanded ? "Read less" : "Read more";
      btn.setAttribute("aria-expanded", expanded ? "true" : "false");
    });

    // Keyboard: Enter/Space already trigger click on a real <button>, so no extra handler needed.
  }

  /* -------------------------------- Boot -------------------------------- */

  function boot() {
    bindReadMoreDelegation();
    document.querySelectorAll("[data-stanlee-reviews]").forEach(function (el) {
      new StanleeReviews(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Re-init when section is added/re-rendered in Shopify theme editor
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", function (e) {
      var el = e.target.querySelector("[data-stanlee-reviews]");
      if (el) new StanleeReviews(el);
    });
  }
})();
